/**
 * @fileoverview Hooks e utilitários para filtrar dados por empresa
 * 
 * Fornece funções para filtrar ValuationSnapshot conforme visualização selecionada:
 * - Consolidado: Dados completos (padrão)
 * - Sétima: Apenas dados da Sétima
 * - 555: Apenas dados do 555 Studios
 * - Comparação: Dados estruturados para comparação lado a lado
 */

import { useState, useMemo } from 'react';
import type { ValuationSnapshot } from '../types/valuation';

export type CompanyView = 'consolidated' | 'setima' | '555' | 'comparison';

/**
 * Hook para gerenciar estado da visualização por empresa
 */
export const useCompanyView = (initialView: CompanyView = 'consolidated') => {
  const [viewMode, setViewMode] = useState<CompanyView>(initialView);

  return {
    viewMode,
    setViewMode,
    isConsolidated: viewMode === 'consolidated',
    isSetimaOnly: viewMode === 'setima',
    is555Only: viewMode === '555',
    isComparison: viewMode === 'comparison',
  };
};

/**
 * Dados de uma empresa individual para comparação
 */
export interface CompanyData {
  name: string;
  label: string;
  ev: number;
  equity: number;
  equityAjustado: number;
  wacc: number;
  margemEBITDA?: number;
  receita?: number;
  ebitda?: number;
  fcff?: number;
}

/**
 * Resultado de comparação lado a lado
 */
export interface ComparisonData {
  setima: CompanyData;
  studios555: CompanyData;
  consolidated: {
    ev: number;
    equity: number;
    equityAjustado: number;
    waccMix: number;
  };
}

/**
 * Filtra snapshot para mostrar apenas dados da Sétima
 */
export function filterSetimaOnly(snapshot: ValuationSnapshot): ValuationSnapshot {
  const setimaWeight = snapshot.setimaValuation.ev / snapshot.consolidado.EV_Total;
  
  return {
    ...snapshot,
    consolidado: {
      ...snapshot.consolidado,
      EV_Total: snapshot.setimaValuation.ev,
      Equity: snapshot.consolidado.Equity * setimaWeight,
      Equity_Ajustado: snapshot.consolidado.Equity_Ajustado * setimaWeight,
      WACC_Mix: snapshot.setimaValuation.wacc,
      equityBridge: {
        ev: snapshot.setimaValuation.ev,
        menos_divida: snapshot.consolidado.equityBridge.menos_divida * setimaWeight,
        mais_caixa: snapshot.consolidado.equityBridge.mais_caixa * setimaWeight,
        equity: snapshot.consolidado.Equity * setimaWeight,
      },
    },
    // Manter apenas valuation da Sétima
    studios555Valuation: {
      ...snapshot.studios555Valuation,
      ev: 0,
    },
  };
}

/**
 * Filtra snapshot para mostrar apenas dados do 555 Studios
 */
export function filter555Only(snapshot: ValuationSnapshot): ValuationSnapshot {
  const studios555Weight = snapshot.studios555Valuation.ev / snapshot.consolidado.EV_Total;
  
  return {
    ...snapshot,
    consolidado: {
      ...snapshot.consolidado,
      EV_Total: snapshot.studios555Valuation.ev,
      Equity: snapshot.consolidado.Equity * studios555Weight,
      Equity_Ajustado: snapshot.consolidado.Equity_Ajustado * studios555Weight,
      WACC_Mix: snapshot.studios555Valuation.wacc,
      equityBridge: {
        ev: snapshot.studios555Valuation.ev,
        menos_divida: snapshot.consolidado.equityBridge.menos_divida * studios555Weight,
        mais_caixa: snapshot.consolidado.equityBridge.mais_caixa * studios555Weight,
        equity: snapshot.consolidado.Equity * studios555Weight,
      },
    },
    // Manter apenas valuation do 555
    setimaValuation: {
      ...snapshot.setimaValuation,
      ev: 0,
    },
  };
}

/**
 * Prepara dados para comparação lado a lado
 */
export function prepareSideBySide(snapshot: ValuationSnapshot): ComparisonData {
  // Calcular equity proporcional por empresa
  const setimaWeight = snapshot.setimaValuation.ev / snapshot.consolidado.EV_Total;
  const studios555Weight = snapshot.studios555Valuation.ev / snapshot.consolidado.EV_Total;
  
  const setima: CompanyData = {
    name: 'setima',
    label: 'Sétima',
    ev: snapshot.setimaValuation.ev,
    equity: snapshot.consolidado.Equity * setimaWeight,
    equityAjustado: snapshot.consolidado.Equity_Ajustado * setimaWeight,
    wacc: snapshot.setimaValuation.wacc,
    // Adicionar mais dados se disponíveis
  };

  const studios555: CompanyData = {
    name: '555',
    label: '555 Studios',
    ev: snapshot.studios555Valuation.ev,
    equity: snapshot.consolidado.Equity * studios555Weight,
    equityAjustado: snapshot.consolidado.Equity_Ajustado * studios555Weight,
    wacc: snapshot.studios555Valuation.wacc,
    // Adicionar mais dados se disponíveis
  };

  const consolidated = {
    ev: snapshot.consolidado.EV_Total,
    equity: snapshot.consolidado.Equity,
    equityAjustado: snapshot.consolidado.Equity_Ajustado,
    waccMix: snapshot.consolidado.WACC_Mix,
  };

  return {
    setima,
    studios555,
    consolidated,
  };
}

/**
 * Hook para filtrar dados conforme visualização selecionada
 */
export const useFilteredData = <T extends { snapshot: ValuationSnapshot }>(
  data: T[],
  viewMode: CompanyView
) => {
  return useMemo(() => {
    if (viewMode === 'consolidated') {
      return data;
    }

    if (viewMode === 'setima') {
      return data.map(item => ({
        ...item,
        snapshot: filterSetimaOnly(item.snapshot),
      }));
    }

    if (viewMode === '555') {
      return data.map(item => ({
        ...item,
        snapshot: filter555Only(item.snapshot),
      }));
    }

    // viewMode === 'comparison'
    return data;
  }, [data, viewMode]);
};

/**
 * Hook para preparar dados de comparação
 */
export const useComparisonData = (
  data: { snapshot: ValuationSnapshot }[]
) => {
  return useMemo(() => {
    return data.map(item => ({
      ...item,
      comparison: prepareSideBySide(item.snapshot),
    }));
  }, [data]);
};

/**
 * Calcula percentual de participação de uma empresa no total
 */
export function calculateCompanyWeight(
  companyEV: number,
  totalEV: number
): number {
  if (totalEV === 0) return 0;
  return (companyEV / totalEV) * 100;
}

/**
 * Formata label com peso da empresa
 */
export function formatCompanyLabel(
  name: string,
  ev: number,
  totalEV: number
): string {
  const weight = calculateCompanyWeight(ev, totalEV);
  return `${name} (${weight.toFixed(1)}%)`;
}
